import React, { useState } from "react";
import { getForgotPassword, postLogin } from "../../network/APIs/login";
import {
  X,
  Briefcase,
  CheckCircle2,
  ArrowRight,
  Lock,
  Mail,
  ArrowLeft,
} from "lucide-react";
import {
  Box,
  AlertColor,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { GoogleOAuthProvider, useGoogleLogin } from "@react-oauth/google";
import forget_password from "./../../assets/images/forget_password.png";
import axios from "axios";

const GoogleAuthButton = ({
  onLoginSuccess,
}: {
  onLoginSuccess: (userData: any) => void;
}) => {
  const login = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      try {
        const res = await axios.get(
          "https://www.googleapis.com/oauth2/v3/userinfo",
          {
            headers: {
              Authorization: `Bearer ${codeResponse.access_token}`,
            },
          },
        );
        // console.log("User Details:", res.data);
        onLoginSuccess(res.data);
      } catch (error) {
        console.error("Error fetching user details:", error);
        alert(
          "Google authentication successful, but failed to fetch user info.",
        );
      }
    },
    onError: () => {
      console.error("Google login failed");
      alert("Google authentication failed!");
    },
  });

  return (
    <button
      type="button"
      onClick={() => login()}
      className="w-full bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold py-3.5 rounded-xl transition-all flex items-center justify-center gap-3"
    >
      <svg className="w-5 h-5" viewBox="0 0 24 24">
        <path
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          fill="#4285F4"
        />
        <path
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          fill="#34A853"
        />
        <path
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          fill="#FBBC05"
        />
        <path
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          fill="#EA4335"
        />
      </svg>
      Google
    </button>
  );
};

interface LoginPageProps {
  onBack: () => void;
  onLoginSuccess?: () => void;
  onSignupClick?: () => void;
}

const validationSchema = Yup.object({
  email: Yup.string()
    .email("Invalid email format")
    .required("Please enter your email address")
    .test("no-script", "Email cannot contain scripts or HTML tags", (value) => {
      return !/<[^>]*>|javascript:|on\w+\s*=/i.test(value || "");
    }),
  password: Yup.string()
    .required("Please enter your password")
    .test("no-script", "Password cannot contain scripts or HTML tags", (value) => {
      return !/<[^>]*>|javascript:|on\w+\s*=/i.test(value || "");
    }),
});

const LoginPage: React.FC<LoginPageProps> = ({
  onBack,
  onLoginSuccess,
  onSignupClick,
}) => {
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] =
    useState<AlertColor>("success");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  const onGoogleLoginSuccess = async (userData: any) => {
    // console.log("Google Login Data:", userData);
    // You can handle user data here (e.g., save to state, local storage, or send to backend)
    setLoading(true);
    setErrorMessage("");

    

    try {
      const formData = new FormData();
      formData.append("email", userData.email);
      formData.append("social_id", userData.sub);
      formData.append("profile_pict", userData.picture);
      formData.append("LoginBy", "GM");
      formData.append("name", userData.name);
      const res = await postLogin(formData);

      setLoading(false);
      if (res.success === false) {
        setErrorMessage(res.message || "Login failed");
      } else {
        const expiryTime = new Date().getTime() + 30 * 24 * 60 * 60 * 1000; // 30 days from now
        localStorage.setItem(
          "Freshment_user_data",
          JSON.stringify({ data: { ...res.data, LoginBy: "GM" }, expiry: expiryTime }),
        );
        window.dispatchEvent(new Event("userLoggedIn"));

        formik.resetForm();
        setErrorMessage("");
        onLoginSuccess?.();
        onBack();
      }
    } catch (error: any) {
      setLoading(false);
      setErrorMessage("An error occurred. Please try again.");
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!forgotEmail || !emailRegex.test(forgotEmail.trim())) {
      setSnackbarMessage("Please enter a valid email address");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      return;
    }

    setForgotLoading(true);

    try {
      const res = await getForgotPassword(forgotEmail);
      setForgotLoading(false);
      if (res.success) {
        setSnackbarMessage(
          res.message ?? "Password reset link sent! Check your inbox.",
        );
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
        setTimeout(() => {
          setShowForgotPassword(false);
          setForgotEmail("");
        }, 4000);
      } else {
        setSnackbarMessage(
          res.message || "Something went wrong. Please try again.",
        );
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      }
    } catch (error) {
      setForgotLoading(false);
      setSnackbarMessage("An error occurred. Please try again.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

  const handleCloseSnackbar = (event) => {
    // if (reason === "clickaway") {
    //   return;
    // }
    setOpenSnackbar(false);
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setErrorMessage("");

      try {
        const formData = new FormData();
        formData.append("email", values.email.trim());
        formData.append("password", values.password);

        const res = await postLogin(formData);

        setLoading(false);
        if (res.success === false) {
          setErrorMessage(res.message || "Login failed");
        } else {
          const expiryTime = new Date().getTime() + 30 * 24 * 60 * 60 * 1000; // 30 days from now
          localStorage.setItem(
            "Freshment_user_data",
            JSON.stringify({ data: res.data, expiry: expiryTime }),
          );
          window.dispatchEvent(new Event("userLoggedIn"));

          formik.resetForm();
          setErrorMessage("");
          onLoginSuccess?.();
          onBack();
        }
      } catch (error: any) {
        setLoading(false);
        setErrorMessage("An error occurred. Please try again.");
      }
    },
  });

  if (showForgotPassword) {
    return (
      <div className="fixed inset-0 z-[10010] bg-slate-50/95 backdrop-blur-xl flex items-center justify-center p-4 overflow-y-auto w-full h-full">
        <div className="bg-white w-full max-w-md rounded-3xl shadow-[0_20px_50px_-12px_rgba(0,0,0,0.1)] overflow-hidden relative border border-slate-100 shrink-0">
          {/* Back Button */}
          <button
            onClick={() => setShowForgotPassword(false)}
            className="absolute top-4 left-4 p-2 mb-5 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition z-10"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>

          <div className="grid gap-5 place-items-center text-center pt-20 px-5 pb-4">
            {/*<img src={forget_password} alt="Forget Passowrd" />*/}
            <h2 className="text-2xl font-bold text-slate-700">
              Forgot Password
            </h2>
            <p className="text-slate-500">
              Enter your email address and we'll send you a link to reset your
              password.
            </p>
          </div>

          <div className="p-8 md:p-10 relative">
            <form onSubmit={handleForgotSubmit}>
              <div className="space-y-5">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider ml-1">
                    Email
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Mail
                        className={`h-5 w-5 transition-colors ${snackbarSeverity === "error" && openSnackbar
                          ? "text-red-400"
                          : "text-slate-400 group-focus-within:text-brand-600"
                          }`}
                      />
                    </div>
                    <input
                      type="text"
                      name="email"
                      className={`block w-full pl-11 pr-4 py-3.5 bg-slate-50 border rounded-xl text-slate-900 text-sm font-semibold placeholder-slate-400 focus:outline-none focus:ring-2 transition-all ${snackbarSeverity === "error" && openSnackbar
                        ? "border-red-400 focus:ring-red-500/20 focus:border-red-500"
                        : "border-slate-200 focus:ring-brand-500/20 focus:border-brand-500"
                        }`}
                      placeholder="name@example.com"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                className="w-full mt-7 bg-brand-600 hover:bg-brand-700 text-white font-bold py-4 rounded-xl shadow-lg shadow-brand-500/30 transition-all transform active:scale-[0.98] flex items-center justify-center gap-2"
              >
                Submit
              </button>

              {forgotLoading && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 10,
                    borderRadius: "24px",
                  }}
                >
                  <CircularProgress size={50} sx={{ color: "#0284c7" }} />
                </Box>
              )}
            </form>
          </div>
        </div>
        <Snackbar
          open={openSnackbar}
          autoHideDuration={5000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbarSeverity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[10010] bg-slate-50/95 backdrop-blur-xl flex items-center justify-center p-4 overflow-y-auto w-full h-full">
      <div className="bg-white w-full max-w-md rounded-3xl shadow-[0_20px_50px_-12px_rgba(0,0,0,0.1)] overflow-hidden relative border border-slate-100 shrink-0">
        {/* Close Button */}
        <button
          onClick={onBack}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition z-10"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="p-8 md:p-10 relative">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-brand-50 text-brand-600 mb-6 shadow-sm border border-brand-100">
              <Briefcase className="w-7 h-7" />
            </div>
            <h1 className="text-3xl font-black text-slate-900 tracking-tight mb-2 font-sans">
              Welcome Back
            </h1>
            <p className="text-slate-500 text-sm font-medium font-sans">
              Login to access your dashboard & jobs
            </p>
          </div>

          <Box component="form" onSubmit={formik.handleSubmit}>
            <div className="space-y-5">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wider ml-1">
                  Email or Username
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail
                      className={`h-5 w-5 transition-colors ${formik.touched.email && formik.errors.email ? "text-red-400" : "text-slate-400 group-focus-within:text-brand-600"}`}
                    />
                  </div>
                  <input
                    type="text"
                    name="email"
                    className={`block w-full pl-11 pr-4 py-3.5 bg-slate-50 border rounded-xl text-slate-900 text-sm font-semibold placeholder-slate-400 focus:outline-none focus:ring-2 transition-all ${formik.touched.email && formik.errors.email
                      ? "border-red-400 focus:ring-red-500/20 focus:border-red-500"
                      : "border-slate-200 focus:ring-brand-500/20 focus:border-brand-500"
                      }`}
                    placeholder="name@example.com"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>
                {formik.touched.email && formik.errors.email && (
                  <p className="text-red-500 text-xs font-medium ml-1 mt-1">
                    {String(formik.errors.email)}
                  </p>
                )}
              </div>

              {/* Password Field */}
              <div className="space-y-1.5">
                <div className="flex justify-between items-center ml-1">
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Password
                  </label>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      setShowForgotPassword(true);
                    }}
                    className="text-xs font-semibold text-brand-600 hover:text-brand-700 hover:underline"
                  >
                    Forgot Password?
                  </button>
                </div>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock
                      className={`h-5 w-5 transition-colors ${formik.touched.password && formik.errors.password ? "text-red-400" : "text-slate-400 group-focus-within:text-brand-600"}`}
                    />
                  </div>
                  <input
                    type="password"
                    name="password"
                    className={`block w-full pl-11 pr-4 py-3.5 bg-slate-50 border rounded-xl text-slate-900 text-sm font-semibold placeholder-slate-400 focus:outline-none focus:ring-2 transition-all ${formik.touched.password && formik.errors.password
                      ? "border-red-400 focus:ring-red-500/20 focus:border-red-500"
                      : "border-slate-200 focus:ring-brand-500/20 focus:border-brand-500"
                      }`}
                    placeholder="••••••••"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </div>
                {formik.touched.password && formik.errors.password && (
                  <p className="text-red-500 text-xs font-medium ml-1 mt-1">
                    {String(formik.errors.password)}
                  </p>
                )}
              </div>
            </div>

            {errorMessage !== "" ? (
              <p className="text-red-600 text-sm mt-2">{errorMessage}</p>
            ) : null}

            {/* Login Button */}
            <button
              type="submit"
              className="w-full mt-5 bg-brand-600 hover:bg-brand-700 text-white font-bold py-4 rounded-xl shadow-lg shadow-brand-500/30 transition-all transform active:scale-[0.98] flex items-center justify-center gap-2"
            >
              Login to Account <ArrowRight className="w-5 h-5" />
            </button>

            {loading && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundColor: "rgba(255, 255, 255, 0.7)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 10,
                  borderRadius: "24px", // To match the modal's border radius roughly
                }}
              >
                <CircularProgress size={50} sx={{ color: "#0284c7" }} />
              </Box>
            )}
          </Box>

          {/* Divider */}
          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-3 text-slate-400 font-bold tracking-widest">
                Or continue with
              </span>
            </div>
          </div>

          {/* Google Login */}
          {/* process.env.REACT_APP_GOOGLE_CLIENT_ID */}
          <GoogleOAuthProvider clientId="420331851809-erj4b5md0n3qb7kgi8uo31k827d1ou0c.apps.googleusercontent.com"
          >
            <GoogleAuthButton onLoginSuccess={onGoogleLoginSuccess} />
          </GoogleOAuthProvider>

          {/* Footer Links */}
          <div className="mt-8 text-center">
            <p className="text-slate-500 text-sm font-medium">
              Don't have an account?{" "}
              <button
                type="button"
                onClick={onSignupClick ? onSignupClick : onBack}
                className="text-brand-600 font-bold hover:underline"
              >
                Sign up for free
              </button>
            </p>
          </div>

          {/* Trust Badge */}
          <div className="mt-8 flex items-center justify-center gap-2 text-[11px] font-bold text-slate-400 uppercase tracking-widest bg-slate-50 py-2 rounded-lg">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
            Secure & Encrypted
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
